import { useEffect, useState, type ReactNode } from "react";
import ComposeBox from "../ComposeBox";
import GmailHeading from "./GmailHeading";
import { useEmailContext, type EmailOption } from "./EmailContext";
import type { EmailData } from "./index";
import { useTheme } from "../../context/ThemeContext";

function GmailWrapperContent({
  initialData,
  isReply,
  fetchEmailOptions,
  handleChange,
  leftChildren,
  rightChildren,
  customTool,
  editorChildren,
  isEditorVisible = true,
  showOnlyBody = false,
  aiContentComponent,
  aiContent,
  showAiPreview,
  onAiInsert,
  onAiDiscard,
  placeholder,
  minHeight = "400px",
  showAttachFile = true,
}: {
  initialData?: Partial<EmailData>;
  fetchEmailOptions: (currentEmail: string) => Promise<EmailOption[]>;
  handleChange: (emailData: EmailData) => void;
  leftChildren?: ReactNode;
  rightChildren?: ReactNode;
  customTool?: ReactNode;
  isReply?: boolean;
  editorChildren?: ReactNode;
  isEditorVisible?: boolean;
  showOnlyBody?: boolean;
  // AI Integration
  aiContentComponent?: ReactNode;
  aiContent?: string;
  showAiPreview?: boolean;
  onAiInsert?: (content: string) => void;
  onAiDiscard?: () => void;
  placeholder?: string;
  minHeight?: string;
  showAttachFile?: boolean;
}) {
  const {
    emails,
    subject,
    attachments,
    setEmails,
    setSubject,
    setAttachments,
    setIsReply,
  } = useEmailContext();
  const { theme } = useTheme();
  const [bodyHTML, setBodyHTML] = useState(initialData?.body || "");
  const [emailData, setEmailData] = useState<EmailData>({
    to: initialData?.to || [],
    cc: initialData?.cc || [],
    bcc: initialData?.bcc || [],
    subject: initialData?.subject || "",
    body: initialData?.body || "",
    attachments: initialData?.attachments || [],
    isReply: isReply || false,
  });

  // Initialize context values from initialData on mount
  useEffect(() => {
    if (initialData) {
      if (initialData.to || initialData.cc || initialData.bcc) {
        setEmails({
          to: initialData.to || [],
          cc: initialData.cc || [],
          bcc: initialData.bcc || [],
        });
      }
      if (initialData.subject) {
        setSubject(initialData.subject);
      }
      if (initialData.attachments) {
        setAttachments(initialData.attachments);
      }
      if (initialData.body) {
        setBodyHTML(initialData.body);
      }
    }
    if (isReply !== undefined) {
      setIsReply(isReply);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReply]);

  const handleEditorChange = (html: string) => {
    setBodyHTML(html);
  };

  useEffect(() => {
    setEmailData((prev) => ({
      ...prev,
      to: emails.to,
      cc: emails.cc,
      bcc: emails.bcc,
    }));
  }, [emails]);

  useEffect(() => {
    setEmailData((prev) => ({
      ...prev,
      subject,
    }));
  }, [subject]);

  useEffect(() => {
    setEmailData((prev) => ({
      ...prev,
      body: bodyHTML,
    }));
  }, [bodyHTML]);

  useEffect(() => {
    setEmailData((prev) => ({
      ...prev,
      attachments,
    }));
  }, [attachments]);

  useEffect(() => {
    setEmailData((prev) => ({
      ...prev,
      isReply,
    }));
  }, [isReply]);

  useEffect(() => {
    handleChange(emailData);
  }, [emailData, handleChange]);

  return (
    <div className="react-mail-inbox-root gmail-wrapper" data-theme={theme}>
      <div className="mail-inbox-internal">
        {!showOnlyBody ? (
          <GmailHeading fetchEmailOptions={fetchEmailOptions} />
        ) : null}
        <ComposeBox
          initialHTML={initialData?.body}
          onChange={handleEditorChange}
          editorChildren={editorChildren}
          rightChildren={rightChildren}
          leftChildren={leftChildren}
          customTool={customTool}
          isEditorVisible={isEditorVisible}
          minHeight={minHeight}
          placeholder={placeholder}
          aiContentComponent={aiContentComponent}
          aiContent={aiContent}
          showAiPreview={showAiPreview}
          onAiInsert={onAiInsert}
          onAiDiscard={onAiDiscard}
          showAttachFile={showAttachFile}
        />
      </div>
    </div>
  );
}

export default GmailWrapperContent;
